import React, { useEffect, useState } from 'react';
import {
  IconButton,
  makeStyles,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState } from 'store';
import { StateEnumDto, UserModelDto } from '.generated/api';
import { useTranslation } from 'react-i18next';

import { primaryColor } from 'theme/theme';
import { CreateEditUserDialog } from 'features/organizations/organizationTabsContent/CreateEditUserDialog';
import 'translations/translations';
import 'theme/iconClasses.css';
import clsx from 'clsx';
import { ApiService } from 'services/apiService';
import { useParams } from 'react-router-dom';
import { organizationActions } from 'actions/organizationActions';
import { notificationActions } from 'actions/notificationActions';

const useStyles = makeStyles((theme) => ({
  alignRight: {
    textAlign: 'right',
  },
  marginRight: {
    marginRight: '0.6rem',
  },
  root: {
    padding: '0.1rem 1rem',
    minWidth: '8.5rem',
  },
  plusIcon: {
    display: 'inline-block',
    fontSize: '1.5rem',
    lineHeight: 0,
    marginRight: '0.5rem',
  },
  addButton: { marginLeft: '0' },
  actionsButton: {
    padding: '0',
  },
  buttonRoot: {
    marginRight: 0,
  },
  tableContainer: {
    marginTop: '1rem',
    marginLeft: '-1.5rem',
    width: 'calc(100% + 3rem)',
  },
  table: {
    color: primaryColor(),
  },
  tableHead: {
    backgroundColor: 'rgba(240, 241, 243, 1)',
    color: primaryColor(),
  },
  tableHeadCell: {
    fontWeight: 'bold',
    color: primaryColor(),
    padding: '0.7rem 1rem 0.5rem 1rem',
  },
  tableCell: {
    color: primaryColor(),
  },
  tableRow: {
    textDecoration: 'none',
  },
  rowInactive: {
    background: 'rgba(240, 241, 243, 1)',
    opacity: '0.6',
  },
  textBold: {
    fontWeight: 'bold',
    color: primaryColor(),
  },
  preDataView: {
    marginTop: '3rem',
    textAlign: 'center',
  },
  error: {
    color: theme.palette.error.main,
  },
  paper: {
    padding: '0',
  },
}));

interface ParamTypes {
  orgId: string;
}

const UsersTabTable: React.FC = () => {
  const { t } = useTranslation(['translations']);
  const classes = useStyles();
  const { orgId } = useParams<ParamTypes>();
  const dispatch = useDispatch();
  const organization = useSelector(
    (state: ApplicationState) => state.organization?.organization,
  );
  const [users, setUsers] = useState<UserModelDto[]>([]);
  const [user, setUser] = useState<UserModelDto>();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  const handleOpenDialog = () => {
    setIsMenuOpen(false);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleToggleActive = () => {
    if (!user) {
      return;
    }

    if (user.state === StateEnumDto.ACTIVE) {
      ApiService.deactivateUserInOrganization(orgId, user.userEmail)
        .then(() => {
          setIsMenuOpen(false);
          dispatch(organizationActions.getOrganizationById(orgId));
        })
        .catch((error) => {
          dispatch(notificationActions.enqueueErrorResponse(error));
        });
    } else {
      ApiService.activateUserInOrganization(orgId, user.userEmail)
        .then(() => {
          setIsMenuOpen(false);
          dispatch(organizationActions.getOrganizationById(orgId));
        })
        .catch((error) => {
          dispatch(notificationActions.enqueueErrorResponse(error));
        });
    }
  };

  const handleResendConfirmationEmail = () => {
    if (!user) {
      return;
    }

    ApiService.resendConfirmationEmailInOrganization(orgId, user.userEmail)
      .then((response) => {
        setIsMenuOpen(false);
        dispatch(organizationActions.getOrganizationById(orgId));
        const message =
          response.data?.status === 'already_confirmed'
            ? t('organization_page.users.labels.email_already_confirmed')
            : t('organization_page.users.labels.confirmation_email_sent');
        dispatch(notificationActions.enqueueSnackbar(message));
      })
      .catch((error) => {
        dispatch(notificationActions.enqueueErrorResponse(error));
      });
  };

  const handleShowContextActions = (
    event: React.MouseEvent<HTMLButtonElement>,
    userRow: UserModelDto,
  ) => {
    setUser(userRow);

    setAnchorEl(event.currentTarget);
    setIsMenuOpen(true);
  };

  const handleHideContextActions = () => {
    setIsMenuOpen(false);
  };

  useEffect(() => {
    if (organization?.users) {
      setUsers(organization?.users);
    }
  }, [organization?.users]);

  return (
    <>
      <TableContainer className={classes.tableContainer}>
        <Table aria-label="organization table" className={classes.table}>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell className={classes.tableHeadCell}>
                {t('organization_page.users.table.user_name')}
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                {t('organization_page.users.table.user_email')}
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                {t('organization_page.users.table.user_phone')}
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                {t('organization_page.users.table.user_roles')}
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                {t('organization_page.users.table.user_state')}
              </TableCell>
              <TableCell className={classes.tableHeadCell} align="right">
                {t('common.actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users?.map((userRow: UserModelDto, index: number) => (
              <TableRow
                key={index}
                hover={true}
                className={clsx(
                  classes.tableRow,
                  userRow.state === StateEnumDto.INACTIVE &&
                    classes.rowInactive,
                )}
              >
                <TableCell className={classes.textBold}>
                  {userRow.name}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {userRow.userEmail}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {userRow.phone}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {userRow.roles.map((role, index) => (
                    <React.Fragment key={index}>
                      <span>{t(`enums.roles.${role}`)}</span>
                      <br />
                    </React.Fragment>
                  ))}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {userRow.state}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={(e) => handleShowContextActions(e, userRow)}
                    color="secondary"
                    className={classes.actionsButton}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                  <Menu
                    keepMounted
                    open={isMenuOpen}
                    onClose={handleHideContextActions}
                    anchorEl={anchorEl}
                    classes={{
                      paper: classes.paper,
                    }}
                  >
                    <MenuItem onClick={handleOpenDialog}>
                      {t('common.edit')}
                    </MenuItem>
                    <MenuItem onClick={handleToggleActive}>
                      {user && user.state === StateEnumDto.ACTIVE
                        ? t('organization_page.users.labels.deactivate')
                        : t('organization_page.users.labels.activate')}
                    </MenuItem>
                    {(user as any)?.emailConfirmed === false && (
                      <MenuItem onClick={handleResendConfirmationEmail}>
                        {t(
                          'organization_page.users.labels.resend_confirmation_email',
                        )}
                      </MenuItem>
                    )}
                  </Menu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <CreateEditUserDialog
        isOpen={isDialogOpen}
        handleClose={handleCloseDialog}
        user={user}
      />
    </>
  );
};

export { UsersTabTable };
